import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import DeleteIcon from "@material-ui/icons/Delete";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";
import IconAction from "./IconAction";
import StatusTag from "./StatusTag";
import useStyles from "./ProfessionalContacts.styles";

const COLUMNS = [
  { key: "beneficiaryName", label: "Beneficiary Name" },
  { key: "accountNumber", label: "Account Number" },
  { key: "sortCode", label: "Sort Code" },
  { key: "bankName", label: "Bank Name" },
];

const BankAccountsTable = ({ bankAccounts, onSetMain, onDelete }) => {
  const classes = useStyles();
  const [order, setOrder] = useState({ key: "beneficiaryName", dir: "asc" });

  const sorted = useMemo(() => {
    const list = [...bankAccounts];
    list.sort((a, b) => {
      const av = (a[order.key] || "").toString();
      const bv = (b[order.key] || "").toString();
      const cmp = av.localeCompare(bv, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return order.dir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [bankAccounts, order]);

  const handleSort = (key) => {
    setOrder((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const deleteButton = (bankAccount) => (
    <A_SecuredAction permission={pageSecurityActions.DELETE}>
      <IconAction
        danger
        title="Delete Bank Account"
        icon={<DeleteIcon fontSize="small" />}
        onClick={() => onDelete(bankAccount)}
      />
    </A_SecuredAction>
  );

  const mainAccountCell = (bankAccount) =>
    bankAccount.isMain ? (
      <StatusTag tone="valid">Main Account</StatusTag>
    ) : (
      <button
        type="button"
        className={classes.linkAction}
        onClick={() => onSetMain(bankAccount.id)}
      >
        Set Main
      </button>
    );

  if (bankAccounts.length <= 1) {
    const bankAccount = bankAccounts[0];
    if (!bankAccount) return null;
    return (
      <div className={classes.rowItem}>
        <div className={classes.rowGrow}>
          <span className={classes.cellStrong}>
            {bankAccount.beneficiaryName || "(unnamed)"}
          </span>
          <div className={classes.subtle}>
            {bankAccount.bankName} · {bankAccount.sortCode} ·{" "}
            {bankAccount.accountNumber}
          </div>
        </div>
        {mainAccountCell(bankAccount)}
        {deleteButton(bankAccount)}
      </div>
    );
  }

  return (
    <div className={classes.tableContainer}>
      <Table size="small" className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell
                key={col.key}
                sortDirection={order.key === col.key ? order.dir : false}
              >
                <TableSortLabel
                  className={classes.sortLabel}
                  active={order.key === col.key}
                  direction={order.key === col.key ? order.dir : "asc"}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {sorted.map((bankAccount) => (
            <TableRow key={bankAccount.id}>
              <TableCell className={classes.cellStrong}>
                {bankAccount.beneficiaryName}
              </TableCell>
              <TableCell className={classes.cellNowrap}>
                {bankAccount.accountNumber}
              </TableCell>
              <TableCell className={classes.cellNowrap}>
                {bankAccount.sortCode}
              </TableCell>
              <TableCell>{bankAccount.bankName}</TableCell>
              <TableCell
                align="right"
                className={clsx(classes.cellActions, classes.cellNowrap)}
              >
                {mainAccountCell(bankAccount)}
                {deleteButton(bankAccount)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

BankAccountsTable.propTypes = {
  bankAccounts: PropTypes.array.isRequired,
  onSetMain: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default BankAccountsTable;
